import { useEffect, useState } from 'react';
import { Edit, Plus, Trash, X } from 'lucide-react';
import { toast } from 'react-toastify';
import type { CombinacionTarifas, Configuracion, TarifaNombrada } from '../../types/configuraciones';
import type { BorradoCombinacionResponse, CombinacionInput, ReprecioTarifaResponse } from '../../services/api/configurationService';
import {
    useCrearCombinacionMutation,
    useActualizarCombinacionMutation,
    useEliminarCombinacionMutation,
} from '../../services/hooks';

const MAX_CLASES = 6;

/**
 * Repartos posibles de clases entre `n` tarifas: al menos una de cada una y
 * como mucho `max` en total. Con 2 tarifas y max 3: 1+1, 1+2, 2+1.
 */
const repartos = (n: number, max: number): number[][] => {
    if (n === 0) return [[]];
    const salida: number[][] = [];
    for (let primera = 1; primera <= max - (n - 1); primera++) {
        for (const resto of repartos(n - 1, max - primera)) salida.push([primera, ...resto]);
    }
    return salida;
};

const clave = (clases: number[]) => clases.join('+');

// Por total de clases y, a igual total, más clases de la primera tarifa antes:
// 1+1, 2+1, 1+2. Es el orden en que los centros escriben su cuadro de precios.
const ordenFilas = (a: number[], b: number[]) => {
    const total = (c: number[]) => c.reduce((s, n) => s + n, 0);
    if (total(a) !== total(b)) return total(a) - total(b);
    const i = a.findIndex((n, k) => n !== b[k]);
    return i === -1 ? 0 : b[i] - a[i];
};

/**
 * Combinaciones de tarifas (ver cotizar en pricingService del back): precio
 * cerrado para quien mezcla tarifas, como el "Híbrido" de un centro de pilates
 * (1 de suelo + 1 de máquina = 89 €, no 45 + 59).
 */
const CombinacionesTarifas = ({ config, tarifas }: { config: Configuracion; tarifas: TarifaNombrada[] }) => {
    const [editando, setEditando] = useState<CombinacionTarifas | 'nueva' | null>(null);
    const [aBorrar, setABorrar] = useState<CombinacionTarifas | null>(null);
    const combinaciones = config.combinaciones || [];
    const nombreTarifa = (id: string) => tarifas.find((t) => t._id === id)?.nombre || 'Tarifa eliminada';

    return (
        <div className="mt-4 pt-4 border-t">
            <div className="flex justify-between items-start gap-4 mb-3">
                <div>
                    <h4 className="font-semibold text-gray-800">Precios combinados</h4>
                    <p className="text-sm text-gray-500 max-w-2xl">
                        Para quien mezcla tarifas y tiene un precio propio en vez de la suma (p. ej. "Híbrido": 1 de suelo
                        + 1 de máquina = 89 €). Si las clases del cliente coinciden con un reparto de la combinación, se
                        cobra ese precio; si tiene esas tarifas pero en un reparto sin precio, no se puede calcular su importe.
                    </p>
                </div>
                <button
                    onClick={() => setEditando('nueva')}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded text-gray-800 bg-white hover:bg-gray-50 shrink-0"
                >
                    <Plus size={16} /> Nueva combinación
                </button>
            </div>

            {combinaciones.length === 0 ? (
                <p className="text-sm text-gray-500 italic">Sin combinaciones: quien mezcla tarifas paga la suma de cada una.</p>
            ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {combinaciones.map((c) => (
                        <div key={c._id} className="border border-gray-300 rounded p-3">
                            <div className="flex justify-between items-start gap-2">
                                <div>
                                    <div className="font-semibold text-gray-800">{c.nombre}</div>
                                    <div className="text-xs text-gray-500">{c.tarifas.map(nombreTarifa).join(' + ')}</div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => setEditando(c)} className="p-1 text-gray-500 hover:text-gray-800" title="Modificar">
                                        <Edit size={16} />
                                    </button>
                                    <button onClick={() => setABorrar(c)} className="p-1 text-gray-500 hover:text-red-600" title="Eliminar">
                                        <Trash size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {[...c.precios].sort((a, b) => ordenFilas(a.clases, b.clases)).map((p) => (
                                    <span key={clave(p.clases)} className="text-sm border border-gray-200 rounded px-2 py-0.5">
                                        {p.clases.join(' + ')} · <strong>{p.price} €</strong>
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editando && (
                <CombinacionModal
                    config={config}
                    combinacion={editando === 'nueva' ? null : editando}
                    tarifas={tarifas}
                    onClose={() => setEditando(null)}
                />
            )}
            {aBorrar && <BorrarCombinacionModal config={config} combinacion={aBorrar} onClose={() => setABorrar(null)} />}
        </div>
    );
};

const TablaCambios = ({ cambios }: { cambios: Array<{ id: string; cliente: string; antes: number; despues: number }> }) => (
    <div className="border rounded max-h-60 overflow-y-auto">
        <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 sticky top-0">
                <tr><th className="text-left px-2 py-1">Cliente</th><th className="text-right px-2 py-1">Antes</th><th className="text-right px-2 py-1">Después</th></tr>
            </thead>
            <tbody>
                {cambios.map((c) => (
                    <tr key={c.id} className="border-t">
                        <td className="px-2 py-1">{c.cliente}</td>
                        <td className="px-2 py-1 text-right">{c.antes} €</td>
                        <td className="px-2 py-1 text-right font-medium">{c.despues} €</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const CombinacionModal = ({
    config, combinacion, tarifas, onClose,
}: {
    config: Configuracion;
    combinacion: CombinacionTarifas | null;
    tarifas: TarifaNombrada[];
    onClose: () => void;
}) => {
    const [nombre, setNombre] = useState(combinacion?.nombre || '');
    // En el orden en que se eligen: es el orden de las clases de cada precio.
    const [tarifasSel, setTarifasSel] = useState<string[]>(combinacion?.tarifas || []);
    const [precios, setPrecios] = useState<Record<string, string>>(
        Object.fromEntries((combinacion?.precios || []).map((p) => [clave(p.clases), String(p.price)]))
    );
    const [preview, setPreview] = useState<ReprecioTarifaResponse | null>(null);
    const crear = useCrearCombinacionMutation();
    const actualizar = useActualizarCombinacionMutation();
    const ocupado = crear.isPending || actualizar.isPending;

    const tarifa = (id: string) => tarifas.find((t) => t._id === id);
    const compartenNivel = (a: string, b: string) =>
        !!tarifa(a)?.niveles.some((n) => tarifa(b)?.niveles.includes(n));

    // Tantas filas como clases ofrezcan las tarifas elegidas: si van hasta 3x,
    // los repartos llegan a 3 en total (1+1, 1+2, 2+1).
    const maxClases = Math.min(MAX_CLASES, Math.max(
        tarifasSel.length,
        ...tarifasSel.flatMap((id) => tarifa(id)?.precios.map((p) => p.weeklySessions) || []),
    ));
    const filas = tarifasSel.length >= 2 ? repartos(tarifasSel.length, maxClases) : [];
    // Un precio guardado fuera de ese rango (p. ej. de antes de bajar la tarifa) también se enseña.
    const extra = (combinacion?.precios || []).map((p) => p.clases).filter((c) => !filas.some((f) => clave(f) === clave(c)));
    const todasLasFilas = [...filas, ...extra].sort(ordenFilas);

    const alternar = (id: string) => {
        setTarifasSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
        setPrecios({});
    };

    const datos = (): CombinacionInput | null => {
        if (!nombre.trim()) { toast.warning('Ponle un nombre a la combinación'); return null; }
        if (tarifasSel.length < 2) { toast.warning('Elige al menos dos tarifas'); return null; }
        const lista = todasLasFilas
            .filter((c) => precios[clave(c)] !== undefined && precios[clave(c)] !== '')
            .map((c) => ({ clases: c, price: Number(precios[clave(c)]) }));
        if (!lista.length) { toast.warning('Pon al menos un precio'); return null; }
        if (lista.some((p) => !Number.isFinite(p.price) || p.price < 0)) { toast.warning('Revisa los precios'); return null; }
        return { nombre: nombre.trim(), tarifas: tarifasSel, precios: lista };
    };

    const guardar = async () => {
        const d = datos();
        if (!d) return;
        try {
            if (!combinacion) {
                await crear.mutateAsync({ configId: config._id, data: d });
                onClose();
                return;
            }
            // Modificar: primero se enseña a quién le cambia el importe.
            if (!preview) {
                const r = await actualizar.mutateAsync({ configId: config._id, combinacionId: combinacion._id, data: { ...d, dryRun: true } });
                if (r.actualizadas === 0 && !r.errores?.length) {
                    await actualizar.mutateAsync({ configId: config._id, combinacionId: combinacion._id, data: d });
                    toast.success('Combinación actualizada');
                    onClose();
                    return;
                }
                setPreview(r);
                return;
            }
            const r = await actualizar.mutateAsync({ configId: config._id, combinacionId: combinacion._id, data: d });
            toast.success(`Combinación actualizada. ${r.actualizadas} cliente${r.actualizadas !== 1 ? 's' : ''} con importe nuevo.`);
            onClose();
        } catch { /* el toast lo pone el hook */ }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">{combinacion ? 'Modificar combinación' : 'Nueva combinación'}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
                </div>

                {preview ? (
                    <div className="space-y-3">
                        <p className="text-sm text-gray-700">
                            Al guardar cambiará el importe de <strong>{preview.actualizadas}</strong> cliente{preview.actualizadas !== 1 ? 's' : ''}
                            {preview.omitidasPrecioFijo > 0 && <> (no se tocan {preview.omitidasPrecioFijo} con precio fijo)</>}.
                        </p>
                        {preview.cambios.length > 0 && <TablaCambios cambios={preview.cambios} />}
                        {preview.errores?.length ? (
                            <div className="text-sm text-red-600">
                                {preview.errores.length} cliente{preview.errores.length !== 1 ? 's' : ''} no se pueden recalcular y se quedan como están:
                                <ul className="list-disc pl-5">
                                    {preview.errores.map((e) => <li key={e.id}>{e.cliente || e.id}: {e.motivo}</li>)}
                                </ul>
                            </div>
                        ) : null}
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setPreview(null)} className="px-4 py-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50">
                                Volver
                            </button>
                            <button onClick={guardar} disabled={ocupado} className="px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded disabled:opacity-50">
                                Confirmar y actualizar
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Nombre</label>
                            <input
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                placeholder="Ej. Híbrido socio fundador"
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tarifas que combina</label>
                            {combinacion ? (
                                <p className="text-sm text-gray-700">
                                    {tarifasSel.map((id) => tarifa(id)?.nombre || 'Tarifa eliminada').join(' + ')}
                                    <span className="block text-xs text-gray-500">Para cambiar las tarifas, borra la combinación y crea otra.</span>
                                </p>
                            ) : (
                                <>
                                    <div className="flex flex-wrap gap-2">
                                        {tarifas.map((t) => {
                                            const activo = tarifasSel.includes(t._id);
                                            // Dos tarifas del mismo nivel (fundador / general) no se combinan: nadie tiene las dos.
                                            const bloqueada = !activo && tarifasSel.some((id) => compartenNivel(id, t._id));
                                            return (
                                                <button
                                                    type="button"
                                                    key={t._id}
                                                    disabled={bloqueada}
                                                    title={bloqueada ? 'Es del mismo nivel que otra tarifa elegida' : undefined}
                                                    onClick={() => alternar(t._id)}
                                                    className={`px-3 py-1 rounded-full text-sm border disabled:opacity-40 ${activo ? 'bg-brand-primary text-white border-transparent' : 'bg-white text-gray-700 border-gray-300'}`}
                                                >
                                                    {t.nombre}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Una combinación por cada variante: "Suelo + Máquina fundador" y "Suelo + Máquina general" son dos.
                                    </p>
                                </>
                            )}
                        </div>

                        {todasLasFilas.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Precio por reparto de clases a la semana</label>
                                <div className="space-y-2">
                                    {todasLasFilas.map((c) => (
                                        <label key={clave(c)} className="flex items-center justify-between gap-3 text-sm text-gray-600">
                                            <span>{c.map((n, i) => `${n} ${tarifa(tarifasSel[i])?.nombre || ''}`).join(' + ')}</span>
                                            <span className="flex items-center shrink-0">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step="0.01"
                                                    value={precios[clave(c)] ?? ''}
                                                    onChange={(e) => setPrecios((prev) => ({ ...prev, [clave(c)]: e.target.value }))}
                                                    className="w-28 rounded-md border-gray-300 shadow-sm"
                                                />
                                                <span className="ml-1">€</span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Deja vacío lo que no se ofrezca.</p>
                            </div>
                        )}

                        {!combinacion && (
                            <p className="text-xs text-gray-500">
                                Crearla no cambia el importe de los clientes actuales. Para aplicarla, usa "Aplicar tarifas a los
                                clientes actuales".
                            </p>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50">
                                Cancelar
                            </button>
                            <button onClick={guardar} disabled={ocupado} className="px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded disabled:opacity-50">
                                {combinacion ? 'Guardar' : 'Crear combinación'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/** Borrar una combinación: sus clientes vuelven a pagar la suma. Primero vista previa. */
const BorrarCombinacionModal = ({ config, combinacion, onClose }: { config: Configuracion; combinacion: CombinacionTarifas; onClose: () => void }) => {
    const eliminar = useEliminarCombinacionMutation();
    const [preview, setPreview] = useState<BorradoCombinacionResponse | null>(null);

    useEffect(() => {
        eliminar.mutateAsync({ configId: config._id, combinacionId: combinacion._id, dryRun: true })
            .then(setPreview)
            .catch(() => onClose());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const confirmar = async () => {
        try {
            await eliminar.mutateAsync({ configId: config._id, combinacionId: combinacion._id, dryRun: false });
            toast.success(`Combinación "${combinacion.nombre}" eliminada`);
            onClose();
        } catch { /* el toast lo pone el hook */ }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Eliminar combinación "{combinacion.nombre}"</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
                </div>

                {!preview ? (
                    <p className="text-sm text-gray-500">Calculando…</p>
                ) : (
                    <div className="space-y-4 text-sm">
                        {preview.clientes === 0 ? (
                            <p className="text-gray-700">Ningún cliente activo se cobra con esta combinación. Se puede eliminar sin más.</p>
                        ) : (
                            <p className="text-gray-700">
                                Se cobra a <strong>{preview.clientes}</strong> cliente{preview.clientes !== 1 ? 's' : ''}. Pasarán a
                                pagar la suma de cada tarifa: {preview.cambios.length} cambian de importe
                                {preview.sinCambios > 0 && <>, {preview.sinCambios} se quedan igual</>}.
                            </p>
                        )}

                        {preview.cambios.length > 0 && <TablaCambios cambios={preview.cambios} />}

                        {preview.sinPrecio.length > 0 && (
                            <div className="text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                                No se puede eliminar: estos clientes se quedarían sin precio para su número de clases.
                                <ul className="list-disc pl-5 mt-1">
                                    {preview.sinPrecio.map((s) => <li key={s.id}>{s.cliente}: {s.motivo}</li>)}
                                </ul>
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50">
                                Cancelar
                            </button>
                            <button
                                onClick={confirmar}
                                disabled={eliminar.isPending || preview.bloqueado}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50"
                            >
                                Eliminar combinación
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CombinacionesTarifas;
